import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Eye, EyeOff } from 'lucide-react';
import { currentSupabaseEnv, setLocalSupabaseEnv } from '@/lib/supabase';
import { safeNext } from '@/lib/safeNext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  // Where to go after signing in, e.g. /alert-action from an alert email. Paths on
  // this site only (safeNext); anything else lands on the dashboard.
  const [searchParams] = useSearchParams();
  const next = safeNext(searchParams.get('next'));

  // Environment detection
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isDemoEnvironment = currentSupabaseEnv === 'demo';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        // Provide helpful message for CORS errors
        const errorMessage = String(error.message || '');
        if (
          errorMessage.includes('CORS') || 
          errorMessage.includes('allowed origins') ||
          errorMessage.includes('Failed to fetch') ||
          (error as { name?: string }).name === 'CorsError'
        ) {
          setError(
            'CORS Configuration Error: Please add http://localhost:5173 to your Supabase project\'s allowed origins.\n\n' +
            'Steps:\n' +
            '1. Go to your Supabase Dashboard\n' +
            '2. Navigate to Project Settings → API\n' +
            '3. Add http://localhost:5173 to the "Allowed Origins" list\n' +
            '4. Save and try again'
          );
        } else {
          setError(errorMessage || 'An error occurred during sign in');
        }
        setLoading(false);
      } else {
        // Back where the person was going, or the dashboard.
        navigate(next ?? '/');
      }
    } catch (err: unknown) {
      // Handle network errors
      if ((err as Error)?.message?.includes('Failed to fetch') || (err as Error)?.message?.includes('CORS')) {
        setError(
          'CORS Configuration Error: Please add http://localhost:5173 to your Supabase project\'s allowed origins.\n\n' +
          'Steps:\n' +
          '1. Go to your Supabase Dashboard\n' +
          '2. Navigate to Project Settings → API\n' +
          '3. Add http://localhost:5173 to the "Allowed Origins" list\n' +
          '4. Save and try again'
        );
      } else {
        setError((err as Error)?.message || 'An unexpected error occurred. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      {/* Card wrapper for positioning the demo chip */}
      <div className="relative w-full max-w-md">
        {/* Demo chip - straddles top border (only on demo) */}
        {isDemoEnvironment && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <span className="px-4 py-1 text-sm font-semibold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 backdrop-blur-sm">
              Demo
            </span>
          </div>
        )}

        <Card className="glow-card w-full">
          <CardHeader className="space-y-1">
            <div className="flex flex-col items-center justify-center mb-4">
              <img
                src="/assets/ht-logo.png"
                alt="Hollywood Trucks"
                className="h-24 w-auto"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </div>

            <CardDescription className="text-center text-muted-foreground">
              Sign in to access the unit dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground text-base"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    className="bg-background/50 border-border text-foreground placeholder:text-muted-foreground pr-10 text-base"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              {error && (
                <div className="text-sm text-red-400 bg-red-950/30 border border-red-900 rounded-md p-3 whitespace-pre-line">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-[#8FB560] hover:bg-[#7da350] text-black"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Environment Switcher - below card, centered */}
      <div className="mt-4 flex flex-col items-center">
        <div className="inline-flex rounded-lg bg-card/30 p-1 border border-border/50">
          <button
            type="button"
            onClick={() => {
              if (isDemoEnvironment) {
                if (isLocalhost) {
                  // On localhost: switch via localStorage and reload
                  setLocalSupabaseEnv('production');
                } else {
                  // On deployed: redirect to production host
                  window.location.href = 'https://app.hollywoodtrucks.com/login';
                }
              }
            }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              !isDemoEnvironment
                ? 'bg-[#8FB560]/20 text-[#8FB560]'
                : 'text-muted-foreground hover:text-muted-foreground/80'
            }`}
          >
            Live
          </button>
          <button
            type="button"
            onClick={() => {
              if (!isDemoEnvironment) {
                if (isLocalhost) {
                  // On localhost: switch via localStorage and reload
                  setLocalSupabaseEnv('demo');
                } else {
                  // On deployed: redirect to demo host
                  window.location.href = 'https://demo.hollywoodtrucks.com/login';
                }
              }
            }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              isDemoEnvironment
                ? 'bg-[#8FB560]/20 text-[#8FB560]'
                : 'text-muted-foreground hover:text-muted-foreground/80'
            }`}
          >
            Demo
          </button>
        </div>
        {isLocalhost && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Local dev: toggle switches Supabase database
          </p>
        )}
      </div>
    </div>
  );
}
